<script setup>
import { computed, ref, watch } from 'vue'
import { ArrowRight, Check, CloudUpload, Database, RefreshCw, ShieldCheck, TriangleAlert } from '@lucide/vue'
import { Badge, Button, Select, Spinner } from '@drama/ui'

import { getNovelPack, getNovelPackRelease, releaseNovelPack } from '../../../api/novel/packs.js'
import { useAdminAuth } from '../../../composables/useAdminAuth.js'
import AuditId from '../audit/AuditId.vue'
import PackReleaseRecords from './PackReleaseRecords.vue'
import { formatBytes, useReleaseText } from './release-text.js'
import '../audit/audit.css'

// 发布到正式环境（C25）：结构同 /game-releases——连接状态与两端版本、版本选择、确认、结果、发布记录与日志。
// 所在的作品预览本身是对话框，确认放在区内展开，不再叠第二层对话框。
const props = defineProps({
  pack: { type: Object, required: true },
  // 后台详情里的版本（新的在前），带说明与按当前规则复核的校验状态。
  versions: { type: Array, required: true },
})

// refreshed：刷新时重取到的作品详情，交给父级替换（版本列表随之更新）。
const emit = defineEmits(['refreshed'])

const { t, tr, apiError, apiErrorLine, dateTime, reasonText } = useReleaseText()
const auth = useAdminAuth()
const isAdmin = computed(() => auth.account.value.role === 'admin')

const loading = ref(false)
const loadError = ref(null)
const state = ref(null)
const versionId = ref('')
const confirming = ref(false)
const publishing = ref(false)
const publishError = ref('')
const result = ref(null)
let loadSeq = 0

const connection = computed(() => {
  if (!state.value) return null
  if (!state.value.configured) return 'not_configured'
  return state.value.connected ? 'connected' : 'unreachable'
})
const selected = computed(() => props.versions.find((version) => version.id === versionId.value) ?? null)
const versionOptions = computed(() => props.versions.map((version) => ({
  value: version.id,
  label: `${version.version} · ${version.note || tr('noNote')} · ${t(`visualGameAdmin.novelPacks.validation.${version.validation_status}`)}`,
})))
// Main 上同 slug 的作品是创作者在 Main 自建的：发布通道不会覆盖它，发布直接禁用（R-13）。
const slugTaken = computed(() => state.value?.main?.release_managed === false)
// 查询 Main 失败的原因：按原因码翻译，原文放 title（R-6）。
const connectionReason = computed(() => (state.value?.connection_error ? reasonText(state.value.connection_error) : null))
const isSameVersion = computed(() => selected.value !== null && state.value?.main?.current_version === selected.value.version)
const canPublish = computed(() => isAdmin.value && connection.value === 'connected' && !slugTaken.value && selected.value?.validation_status === 'valid' && !publishing.value)

watch(() => props.pack.id, () => {
  versionId.value = props.versions[0]?.id ?? ''
  confirming.value = false
  result.value = null
  publishError.value = ''
  load()
}, { immediate: true })

// 版本列表被重取后：选中的版本还在就保持，不在了（或原来没有版本）改选最新的。
watch(() => props.versions, (versions) => {
  if (!versions.some((version) => version.id === versionId.value)) versionId.value = versions[0]?.id ?? ''
})

/**
 * 只认最后一次加载：换了作品或重复刷新时，旧请求的结果丢弃。withDetail 时连作品详情（版本列表）一起重取并交给
 * 父级替换，作者在创作台刚切的版本不必关掉预览就能选。
 */
async function load(withDetail = false) {
  const seq = ++loadSeq
  const packId = props.pack.id
  loading.value = true
  loadError.value = null
  try {
    const [next, detail] = await Promise.all([
      getNovelPackRelease(packId),
      withDetail ? getNovelPack(packId) : null,
    ])
    if (seq !== loadSeq) return
    state.value = next
    if (detail !== null) emit('refreshed', detail)
  } catch (error) {
    if (seq === loadSeq) loadError.value = apiError(error)
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

async function publish() {
  const packId = props.pack.id
  publishing.value = true
  publishError.value = ''
  result.value = null
  try {
    result.value = await releaseNovelPack(packId, selected.value.id)
  } catch (error) {
    publishError.value = apiErrorLine(error)
  } finally {
    publishing.value = false
    confirming.value = false
  }
  if (props.pack.id === packId) await load()
}

function connectionVariant(status) {
  switch (status) {
    case 'connected': return 'success'
    case 'not_configured': return 'warning'
    case 'unreachable': return 'danger'
    default: throw new Error(`Unhandled release connection: ${status}`)
  }
}

</script>

<template>
  <section class="pr" data-testid="novel-pack-release">
    <header class="pr__head">
      <div>
        <h4>{{ tr('title') }}</h4>
        <p class="pr__muted">{{ tr('hint') }}</p>
      </div>
      <Button variant="secondary" size="sm" :loading="loading" data-testid="novel-release-refresh" @click="load(true)">
        <RefreshCw :size="14" aria-hidden="true" /> {{ tr('refresh') }}
      </Button>
    </header>

    <div v-if="loading && !state" class="pr__state"><Spinner /></div>
    <div v-else-if="loadError" class="pr__state">
      <p class="pr__error" :title="loadError.title">{{ loadError.text }}</p>
      <Button variant="secondary" size="sm" @click="load(true)">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else-if="state">
      <div class="pr__compare">
        <div class="pr__env">
          <p class="pr__env-title"><Database :size="16" aria-hidden="true" /> {{ tr('stage') }}</p>
          <dl>
            <div><dt>{{ tr('selectedVersion') }}</dt><dd class="pr__mono">{{ selected?.version ?? '—' }}</dd></div>
            <div><dt>{{ tr('versionNote') }}</dt><dd>{{ selected?.note || tr('noNote') }}</dd></div>
            <div><dt>{{ tr('cutAt') }}</dt><dd>{{ selected ? dateTime(selected.published_at) : '—' }}</dd></div>
          </dl>
        </div>
        <ArrowRight class="pr__arrow" :size="18" aria-hidden="true" />
        <div class="pr__env">
          <p class="pr__env-title">
            <CloudUpload :size="16" aria-hidden="true" /> {{ tr('main') }}
            <Badge size="sm" :variant="connectionVariant(connection)" data-testid="novel-release-connection">{{ tr(`connection.${connection}`) }}</Badge>
          </p>
          <p v-if="connection === 'connected' && slugTaken" class="pr__note" data-testid="novel-release-slug-taken"><TriangleAlert :size="16" aria-hidden="true" /> {{ tr('slugTaken', { slug: pack.slug }) }}</p>
          <dl v-else-if="connection === 'connected'">
            <div><dt>{{ tr('mainVersion') }}</dt><dd class="pr__mono">{{ state.main?.current_version ?? tr('notOnMain') }}</dd></div>
            <div><dt>{{ tr('releasedAt') }}</dt><dd>{{ state.main?.released_at ? dateTime(state.main.released_at) : '—' }}</dd></div>
            <div><dt>{{ tr('targetUrl') }}</dt><dd class="pr__mono">{{ state.target_url }}</dd></div>
          </dl>
          <p v-else class="pr__note" :title="connectionReason?.title"><TriangleAlert :size="16" aria-hidden="true" /> {{ connectionReason?.text ?? tr(`connectionHelp.${connection}`) }}</p>
        </div>
      </div>

      <p v-if="versions.length === 0" class="pr__muted">{{ tr('noVersions') }}</p>
      <div v-else class="pr__action">
        <div class="pr__field">
          <label>{{ tr('versionLabel') }}</label>
          <Select v-model="versionId" :options="versionOptions" :aria-label="tr('versionLabel')" :disabled="publishing" block data-testid="novel-release-version" />
          <p v-if="selected?.validation_status === 'invalid'" class="pr__error">{{ tr('invalidVersion') }}</p>
          <p v-else-if="isSameVersion" class="pr__muted">{{ tr('sameVersion') }}</p>
          <p v-if="!isAdmin" class="pr__muted">{{ tr('adminRequired') }}</p>
        </div>
        <Button v-if="!confirming" variant="primary" :disabled="!canPublish" data-testid="novel-release-open-confirm" @click="confirming = true">
          <CloudUpload :size="16" aria-hidden="true" /> {{ tr('publish') }}
        </Button>
      </div>

      <div v-if="confirming && selected" class="pr__confirm" role="group" :aria-label="tr('confirmTitle')" data-testid="novel-release-confirm">
        <p class="pr__confirm-title"><ShieldCheck :size="18" aria-hidden="true" /> {{ tr('confirmTitle') }}</p>
        <p>{{ tr('confirmBody', { title: pack.title, version: selected.version }) }}</p>
        <p class="pr__muted">{{ tr('versionNote') }}：{{ selected.note || tr('noNote') }}</p>
        <p class="pr__muted">{{ tr('noUserData') }}</p>
        <p v-if="publishing" class="pr__muted" role="status">{{ tr('progress') }}</p>
        <div class="pr__confirm-actions">
          <Button variant="ghost" :disabled="publishing" @click="confirming = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
          <Button variant="primary" :loading="publishing" data-testid="novel-release-confirm-submit" @click="publish">{{ tr('confirm') }}</Button>
        </div>
      </div>
      <p v-if="publishError" class="pr__error" role="alert"><TriangleAlert :size="16" aria-hidden="true" /> {{ publishError }}</p>
      <div v-if="result" class="pr__result" role="status" data-testid="novel-release-result">
        <Check :size="18" aria-hidden="true" />
        <div>
          <p class="pr__confirm-title">{{ tr('successTitle') }}</p>
          <p>{{ tr('successSummary', { version: result.version, copied: result.assets_copied, reused: result.assets_reused, size: formatBytes(result.bytes_copied) }) }}</p>
          <p class="pr__id">{{ tr('releaseId') }} <AuditId :id="result.release_id" /></p>
        </div>
      </div>

      <PackReleaseRecords :pack-id="pack.id" :records="state.records" :is-admin="isAdmin" @finished="load()" />
    </template>
  </section>
</template>

<style scoped>
.pr { display: grid; gap: var(--drama-space-md); padding-top: var(--drama-space-md); border-top: 1px solid var(--drama-color-border); }
.pr__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-md); }
.pr__head h4 { margin: 0 0 var(--drama-space-xs); font-size: var(--drama-font-size-md); }
.pr__head p { margin: 0; }
.pr__state { display: flex; align-items: center; gap: var(--drama-space-md); }
.pr__compare { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: var(--drama-space-md); }
.pr__arrow { color: var(--drama-color-text-muted); }
.pr__env { display: grid; gap: var(--drama-space-sm); padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); min-width: 0; }
.pr__env-title { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-xs); margin: 0; font-weight: var(--drama-font-weight-medium); }
.pr__env dl { display: grid; gap: var(--drama-space-xs); margin: 0; }
.pr__env dl div { display: flex; justify-content: space-between; gap: var(--drama-space-md); min-width: 0; }
.pr__env dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.pr__env dd { margin: 0; overflow-wrap: anywhere; text-align: right; }
.pr__note { display: flex; gap: var(--drama-space-xs); margin: 0; color: var(--drama-color-text-muted); overflow-wrap: anywhere; }
.pr__action { display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--drama-space-md); }
.pr__field { display: grid; gap: var(--drama-space-xs); flex: 1 1 320px; min-width: 0; }
.pr__field label { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.pr__field p { margin: 0; }
.pr__confirm { display: grid; gap: var(--drama-space-sm); padding: var(--drama-space-md); border: 1px solid var(--drama-color-primary); border-radius: var(--drama-radius-md); }
.pr__confirm p { margin: 0; }
.pr__confirm-title { display: flex; align-items: center; gap: var(--drama-space-xs); font-weight: var(--drama-font-weight-medium); }
.pr__confirm-actions { display: flex; justify-content: flex-end; gap: var(--drama-space-sm); }
.pr__result { display: flex; gap: var(--drama-space-sm); padding: var(--drama-space-md); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); color: var(--drama-color-success); }
.pr__result p { margin: 0; color: var(--drama-color-text); }
.pr__id { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.pr__muted { color: var(--drama-color-text-muted); }
.pr__error { display: flex; gap: var(--drama-space-xs); margin: 0; color: var(--drama-color-danger); overflow-wrap: anywhere; }
.pr__mono { font-family: var(--drama-font-family-mono, monospace); overflow-wrap: anywhere; }
@media (max-width: 640px) {
  .pr__compare { grid-template-columns: minmax(0, 1fr); }
  .pr__arrow { transform: rotate(90deg); justify-self: center; }
}
</style>
